// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import {
    arrow,
    autoUpdate,
    flip,
    offset,
    safePolygon,
    shift,
    useClick,
    useDelayGroup,
    useDismiss,
    useFloating,
    useFloatingNodeId,
    useFloatingParentNodeId,
    useFocus,
    useHover,
    useInteractions,
    useRole,
} from "@floating-ui/react";
import { FloatingTooltipProps } from "@synd/App/Components/FloatingTooltip/FloatingTooltip";
import { tooltipDefaultProps } from "@synd/App/Components/FloatingTooltip/presentationProps";
import React, { RefObject, useMemo, useState } from "react";

export const FocusableSelectors = [
    "a[href]",
    "button",
    "textarea",
    'input[type="text"]',
    "select",
    '[tabindex]:not([tabindex="-1"])',
].join(",");

interface UseTooltipProps extends Omit<FloatingTooltipProps, "triggerComponent"> {
    arrowRef?: RefObject<SVGSVGElement | null>;
}

export function useTooltip({
    isOpen = tooltipDefaultProps.isOpen,
    placement = tooltipDefaultProps.placement,
    trigger = tooltipDefaultProps.trigger,
    onOpenChange,
    offset: offsetProps = tooltipDefaultProps.offset,
    shift: shiftProps = tooltipDefaultProps.shift,
    delay: userDelay,
    showArrow = tooltipDefaultProps.showArrow,
    arrowRef,
}: UseTooltipProps) {
    const [open, setOpen] = useState(false);
    const [hoverEnabled, setHoverEnabled] = useState(trigger === "hover");
    const nodeId = useFloatingNodeId();

    React.useEffect(() => {
        if (isOpen !== open) {
            setOpen(isOpen || false);
        }
    }, [isOpen]);

    const floating = useFloating({
        nodeId,
        placement,
        open,
        onOpenChange: isOpen => {
            setOpen(isOpen);
            onOpenChange?.(isOpen);
        },
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(offsetProps),
            flip({
                ...tooltipDefaultProps.flip,
                crossAxis: placement.includes("-"),
                fallbackAxisSideDirection: "start",
            }),
            shift(shiftProps),
            ...(showArrow && arrowRef?.current ? [arrow({ element: arrowRef })] : []),
        ],
    });

    const parentId = useFloatingParentNodeId();
    const isNested = parentId != null;

    const { context } = floating;
    const interactions = useInteractions([
        useHover(context, {
            enabled: trigger === "hover" && hoverEnabled,
            delay: isNested ? userDelay : userDelay || useDelayGroup(context).delay,
            handleClose: safePolygon(),
        }),
        useClick(context, { enabled: trigger === "click" }),
        useFocus(context),
        useDismiss(context),
        useRole(context, { role: "tooltip"}),
    ]);

    return useMemo(
        () => ({
            open,
            setOpen,
            setHoverEnabled,
            ...interactions,
            ...floating,
            nodeId,
        }),
        [open, setOpen, interactions, floating, setHoverEnabled, nodeId],
    );
}
